import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Globe, ChevronDown, X } from "lucide-react";
import { useRegion } from "../contexts/RegionContext";

const Column = ({ items, value, onChange, isDark, width }) => {
  const containerRef = useRef(null);
  const audioContextRef = useRef(null);
  const itemHeight = 44;
  const paddingHeight = 98; // Height to center the item

  useEffect(() => {
    // Initialize Audio Context on mount
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    // Cleanup
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playClickSound = useCallback(() => {
    if (audioContextRef.current) {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioContextRef.current.currentTime + 0.02);
      
      gainNode.gain.setValueAtTime(0.05, audioContextRef.current.currentTime); // Lower volume for subtlety
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + 0.03);

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      oscillator.start();
      oscillator.stop(audioContextRef.current.currentTime + 0.03);
    }
  }, []);

  useEffect(() => {
    const index = items.indexOf(value);
    if (index !== -1 && containerRef.current) {
      containerRef.current.scrollTop = index * itemHeight;
    }
  }, []); // Only scroll on mount for initial position, let user control afterwards

  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    if (items[index] !== undefined && items[index] !== value) {
      onChange(items[index]);
      playClickSound();
    }
  };

  return (
    <div className={`relative h-full overflow-y-auto no-scrollbar snap-y snap-mandatory ${width}`} ref={containerRef} onScroll={handleScroll}>
      <div style={{ height: paddingHeight }} />
      {items.map((item, i) => {
        const index = items.indexOf(value);
        const distance = Math.abs(i - index);
        const opacity = Math.max(0.3, 1 - distance * 0.25);
        const scale = Math.max(0.8, 1 - distance * 0.05);
        const rotate = (i - index) * 15; // 3D Tilt effect

        return (
          <div 
            key={i} 
            className="h-[44px] flex items-center justify-center snap-center transition-all duration-100 px-2"
            style={{ 
              opacity,
              transform: `scale(${scale}) rotateX(${rotate}deg)`,
              color: i === index ? (isDark ? 'white' : 'black') : (isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'),
              fontWeight: i === index ? 'bold' : 'normal',
              fontSize: i === index ? '18px' : '15px'
            }}
          >
            {item}
          </div>
        );
      })}
      <div style={{ height: paddingHeight }} />
    </div>
  );
};

const Footer = ({ footerRef, minimal = false }) => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { region, regions, changeRegion, regionCode } = useRegion();
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [tempRegionCode, setTempRegionCode] = useState(regionCode);
  const regionRef = useRef(null);

  const regionOptions = Object.values(regions).sort((a, b) => a.name.localeCompare(b.name));
  const regionNames = regionOptions.map(r => `${r.flag} ${r.name} (${r.currency})`);
  const currentTempRegionName = regions[tempRegionCode] ? `${regions[tempRegionCode].flag} ${regions[tempRegionCode].name} (${regions[tempRegionCode].currency})` : "";

  useEffect(() => {
    if (showRegionPicker) {
      document.body.style.overflow = 'hidden';
      setTempRegionCode(regionCode);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showRegionPicker, regionCode]);


  
  // Force minimal mode for ecosystem/account or if prop is true
  const isMinimal = minimal || location.pathname === "/account";

  // Static in-place animation variants (no spatial movement to preserve layout stability)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.05,
        delayChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1] }
    }
  };

  return (
    <>
      {!isMinimal && (
        <footer
          ref={footerRef}
          className="w-full bg-black min-h-fit flex flex-col items-center relative z-10 overflow-hidden"
        >
          {/* Rich Shade Glow Effect (Stationary luminous reveal on scroll) */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 h-96 pointer-events-none overflow-hidden"
          >
            {/* Base Deep Blue Glow */}
            <div className="absolute bottom-[-10%] left-1/2 -translate-x-1/2 w-[140%] h-[300px] bg-blue-600/20 blur-[120px] rounded-[100%] opacity-80" />
            {/* Core Bright Light */}
            <div className="absolute bottom-[-5%] left-1/2 -translate-x-1/2 w-[100%] h-[150px] bg-blue-400/30 blur-[80px] rounded-[100%] opacity-60" />
            {/* Horizon Highlight */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/40 to-transparent shadow-[0_0_20px_rgba(147,197,253,0.3)]" />
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-7xl w-full mx-auto px-6 pt-20 pb-12 relative z-10"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-10 gap-y-16 items-start text-left">
              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Try On
                </h4>
                <ul className="space-y-4">
                  <li>
                    <a
                      href="https://taels.fozzil.in"
                      className="text-gray-400 hover:text-white transition-colors text-lg"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Web
                    </a>
                  </li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Shop
                </h4>
                <ul className="space-y-4">
                  <li><Link to="/shop/buy-ark" className="text-gray-400 hover:text-white transition-colors text-lg">Shop ARK</Link></li>
                  <li><Link to="/shop/buy-ark/ark" className="text-gray-400 hover:text-white transition-colors text-lg">Buy ARK</Link></li>
                  <li><Link to="/shop/buy-ark/ark-pro" className="text-gray-400 hover:text-white transition-colors text-lg">Buy ARK Pro</Link></li>
                  <li><Link to="/shop/medos" className="text-gray-400 hover:text-white transition-colors text-lg">Shop MedOS</Link></li>
                  <li><Link to="/shop/intelligence" className="text-gray-400 hover:text-white transition-colors text-lg">Subscribe Intelligence</Link></li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Products
                </h4>
                <ul className="space-y-4">
                  <li><Link to="/fozzil-ark" className="text-gray-400 hover:text-white transition-colors text-lg">Ark</Link></li>
                  <li><Link to="/taels" className="text-gray-400 hover:text-white transition-colors text-lg">T.A.E.L.S</Link></li>
                  <li><Link to="/medos" className="text-gray-400 hover:text-white transition-colors text-lg">MedOS</Link></li>
                  <li><Link to="/zypherai" className="text-gray-400 hover:text-white transition-colors text-lg">Zypher AI</Link></li>
                  <li><Link to="/pricing-taels" className="text-gray-400 hover:text-white transition-colors text-lg">Pricing</Link></li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Research
                </h4>
                <ul className="space-y-4">
                  <li><Link to="/research/ai-museum" className="text-gray-400 hover:text-white transition-colors text-lg">AI Museum</Link></li>
                  <li><Link to="/research/awm" className="text-gray-400 hover:text-white transition-colors text-lg">Awm</Link></li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Stories
                </h4>
                <ul className="space-y-4">
                  <li><Link to="/stories/ark" className="text-gray-400 hover:text-white transition-colors text-lg">The Ark</Link></li>
                  <li><Link to="/stories/company" className="text-gray-400 hover:text-white transition-colors text-lg">The Company</Link></li>
                  <li><Link to="/stories/ai-museum" className="text-gray-400 hover:text-white transition-colors text-lg">Cintel AI Museum</Link></li>
                </ul>
              </motion.div>
              

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Company
                </h4>
                <ul className="space-y-4">
                  <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-lg">About</Link></li>
                  <li><a href="https://support.fozzil.in/contact" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Contact</a></li>
                  <li><Link to="/brand" className="text-gray-400 hover:text-white transition-colors text-lg">Brand</Link></li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Resources
                </h4>
                <ul className="space-y-4">
                  <li><Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Terms</Link></li>
                  <li><Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Privacy</Link></li>
                  <li><a href="https://account.fozzil.in/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Manage Account</a></li>
                  <li><a href="https://ecosystem.fozzil.in/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Ecosystem</a></li>
                  <li><Link to="/legal" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Legal</Link></li>
                  <li><Link to="/ethics-compliance" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Ethics</Link></li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Support
                </h4>
                <ul className="space-y-4">
                  <li><a href="https://support.fozzil.in/help-center" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Help Centre</a></li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h4 className="text-sm font-mono tracking-[0.3em] text-white mb-8 uppercase">
                  Safety
                </h4>
                <ul className="space-y-4">
                  <li><Link to="/security-and-privacy" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-lg">Security</Link></li>
                </ul>
              </motion.div>
              
            </div>

            {/* Region Selector - Narrower line and reduced spacing */}
            <motion.div variants={itemVariants} className="mt-12 flex justify-end">
              <div className="relative border-t border-white/5 pt-6 flex flex-col items-end" ref={regionRef}>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3 pr-2">Country/ Region</p>
                <button
                  onClick={() => setShowRegionPicker(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-transparent border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white text-xs font-medium transition-all cursor-pointer"
                >
                  <Globe size={14} />
                  <span>{region.flag} {region.name} ({region.currency})</span>
                  <ChevronDown size={12} className={`transition-transform ${showRegionPicker ? 'rotate-180' : ''}`} />
                </button>

                {typeof window !== 'undefined' && createPortal(
                  <AnimatePresence>
                    {showRegionPicker && (
                      <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
                        <motion.div 
                          initial={{ opacity: 0 }} 
                          animate={{ opacity: 1 }} 
                          exit={{ opacity: 0 }} 
                          onClick={() => setShowRegionPicker(false)}
                          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        
                        <motion.div
                          initial={{ scale: 0.95, y: 20, opacity: 0 }}
                          animate={{ scale: 1, y: 0, opacity: 1 }}
                          exit={{ scale: 0.95, y: 20, opacity: 0 }}
                          className="relative w-full max-w-sm p-0 rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] flex flex-col border border-white/10 bg-black text-white"
                        >
                          {/* Top Glow */}
                          <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600/15 blur-[80px] pointer-events-none" />
                          
                          <div className="relative z-10">
                            <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
                              <button 
                                onClick={() => setShowRegionPicker(false)} 
                                className="text-gray-400 hover:text-white transition-colors"
                              >
                                <X size={20} />
                              </button>
                              <span className="font-bold tracking-tight">Select Region</span>
                              <button 
                                onClick={() => {
                                  changeRegion(tempRegionCode);
                                  setShowRegionPicker(false);
                                }}
                                className="text-blue-500 font-bold"
                              >
                                Done
                              </button>
                            </div>

                            <div className="relative h-[240px] flex items-center justify-center overflow-hidden">
                              {/* Selection Bars */}
                              <div className="absolute top-1/2 -translate-y-1/2 w-full h-[44px] pointer-events-none border-y border-white/10 z-30" />
                              
                              <div className="flex w-full h-full relative z-20">
                                <Column 
                                  items={regionNames} 
                                  value={currentTempRegionName} 
                                  onChange={(val) => {
                                    const code = regionOptions.find(r => `${r.flag} ${r.name} (${r.currency})` === val)?.code;
                                    if (code) setTempRegionCode(code);
                                  }}
                                  isDark={true}
                                  width="flex-1"
                                />
                              </div>

                              {/* Overlays for fade effect */}
                              <div className="absolute top-0 left-0 w-full h-20 pointer-events-none bg-gradient-to-b from-black to-transparent z-40" />
                              <div className="absolute bottom-0 left-0 w-full h-20 pointer-events-none bg-gradient-to-t from-black to-transparent z-40" />
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </AnimatePresence>,
                  document.body
                )}
              </div>
            </motion.div>
          </motion.div>
        </footer>
      )}
      <footer className="w-full bg-gradient-to-b from-blue-500/30 to-deep-blue flex justify-between items-center px-[4vw] py-4 min-h-[48px] box-border md:flex-row md:gap-0 md:px-[4vw] sm:flex-col sm:gap-2 sm:px-[2vw] relative z-20">
        <div className="font-secondary text-sm text-gray-500 font-medium tracking-widest text-center w-full md:w-auto uppercase">
          <span>FOZZIL &copy; {new Date().getFullYear()}</span>
        </div>


        <div className="flex items-center gap-2 sm:gap-1 justify-center w-full md:w-auto">
          <a
            href="https://www.facebook.com/fozzilai/"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-none transition-colors duration-200 hover:bg-neutral-900"
            aria-label="Facebook"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#fff" />
              <path
                d="M20.5 16H17V25H14V16H12V13.5H14V12.25C14 10.1789 15.1789 9 17.25 9H20V11.5H18.5C17.9477 11.5 17.5 11.9477 17.5 12.5V13.5H20.5V16Z"
                fill="#111"
              />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/fozzilai/"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-none transition-colors duration-200 hover:bg-neutral-900"
            aria-label="Instagram"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#fff" />
              <g>
                <circle cx="16" cy="16" r="6" stroke="#111" strokeWidth="2" />
                <circle cx="22.5" cy="9.5" r="1.5" fill="#111" />
                <rect
                  x="11"
                  y="11"
                  width="10"
                  height="10"
                  rx="5"
                  stroke="#111"
                  strokeWidth="2"
                />
              </g>
            </svg>
          </a>
          <a
            href="https://www.linkedin.com/company/fozzilai"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-none transition-colors duration-200 hover:bg-neutral-900"
            aria-label="LinkedIn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#fff" />
              <g>
                <rect x="11" y="13" width="2.5" height="7" fill="#111" />
                <rect x="18.5" y="16" width="2.5" height="4" fill="#111" />
                <circle cx="12.25" cy="11.25" r="1.25" fill="#111" />
                <rect x="14.5" y="16" width="2.5" height="4" fill="#111" />
              </g>
            </svg>
          </a>
          <a
            href="https://x.com/fozzilai"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-none transition-colors duration-200 hover:bg-neutral-900"
            aria-label="X"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#fff" />
              <path
                d="M12 12L20 20M20 12L12 20"
                stroke="#111"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </a>
          <a
            href="https://www.youtube.com/@FozzilAI"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-none transition-colors duration-200 hover:bg-neutral-900"
            aria-label="YouTube"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="#fff" />
              <path d="M13 12L21 16L13 20V12Z" fill="#111" />
            </svg>
          </a>
        </div>
      </footer>
    </>
  );
};

export default Footer;
